import { useEffect, useMemo } from 'react';
import { css, styled } from 'styled-components';

import { isSameOrAfter, isSameOrBefore } from '@fora-monorepo/date-utils';
import { FAGroupViewModel } from '@fora-monorepo/employer-my-pages-common/types';
import { Box, ForaDatePicker, OptionType, StandardSelect, Text, theme, TooltipAriakit } from '@foradevelopment/fora-ui';

import { useAppSelector } from '../../../redux/hooks';
import { useTypedTranslation } from '../../../i18n/i18n';
import { MarkupSelectAndDateInputProps } from './types';

export const StartDateAndMarkupSelect = ({
  startDateInputValue,
  startDateInputProps,
  markupSelectInput,
  markupOptions,
  disabled,
  disabledDatepicker,
  ariaLabel,
}: MarkupSelectAndDateInputProps) => {
  const { t } = useTypedTranslation();
  const { ffManageEmployeeAndEmployment, ffFaGroupIdOnEmployment } = useAppSelector(state => state.foraGlobal.settings);
  const { filteredMarkupOptions, showOptions, onlyOneOption } = useMemo(() => {
    const filteredMarkupOptions = getFilteredSelectOptions(markupOptions, startDateInputValue, ffFaGroupIdOnEmployment);
    const showOptions = Boolean(startDateInputValue && filteredMarkupOptions.length > 0);
    const onlyOneOption = filteredMarkupOptions.length === 1;

    return { filteredMarkupOptions, showOptions, onlyOneOption };
  }, [ffFaGroupIdOnEmployment, markupOptions, startDateInputValue]);

  useEffect(() => {
    if (!showOptions && (markupSelectInput.props.value || markupSelectInput.props.hasError)) {
      // Reset markup select options disappear AND has a value or errors
      markupSelectInput.props.setValue('');
    }
    // Only do this when showOptions toggles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOptions]);

  useEffect(() => {
    if (onlyOneOption) {
      // Programmatically set selected markup if there's only one result (no Select)
      markupSelectInput.props.setValue(filteredMarkupOptions[0].id);
    }
    // Only do this when onlyOneOption toggles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyOneOption]);

  return (
    <Layout $onlyOneOption={onlyOneOption} data-testid={'start-date-and-markup-select'}>
      <TooltipAriakit
        content={
          <Box>
            <Text fontWeight="bold">{t('tooltips.employment.markupStartDateLocked.title')} </Text>
            <Text>{t('tooltips.employment.markupStartDateLocked.description')} </Text>
          </Box>
        }
        id={'tooltip-markup-start-date'}
        conditionalTooltip={disabledDatepicker}
      >
        <ForaDatePicker
          {...startDateInputProps}
          label={t('common.startDate')}
          disabled={disabledDatepicker}
          hideLabelOnFocus
          ariaLabel={ariaLabel}
          labelBackground="transparent"
          testId={'markup-start-date-picker'}
          width={startDateInputProps.calendarDate && !onlyOneOption ? '100%' : '47%'}
        />
      </TooltipAriakit>

      {showOptions &&
        (onlyOneOption && !ffManageEmployeeAndEmployment ? (
          <OneMarkup faGroup={filteredMarkupOptions[0]} />
        ) : (
          filteredMarkupOptions.length > 1 && (
            <StandardSelect
              testId="test-select-markup"
              label={t('form.addEmployee.markup.selectMarkup')}
              options={filteredMarkupOptions}
              errorMessage={t('errorMessages.validation.selectMarkup')}
              disabled={disabled}
              {...markupSelectInput.props}
            />
          )
        ))}
    </Layout>
  );
};

const OneMarkup = ({ faGroup }: { faGroup: OptionType }) => {
  const { t } = useTypedTranslation();

  return (
    <Box marginTop={40}>
      <Text color="black70" fontWeight="light" size="small">
        {t('form.addEmployee.selectedMarkupOneOption')}
      </Text>
      <Text size="medium">{faGroup.name}</Text>
    </Box>
  );
};

const Layout = styled.div<{ $onlyOneOption: boolean }>`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  column-gap: 40px;
  ${props =>
    !props.$onlyOneOption &&
    css`
      @media screen and (min-width: ${theme.breakpoint.md}px) {
        align-items: flex-start;
        flex-direction: row;
      }
    `}
`;

const getFilteredSelectOptions = (
  markupOptions: FAGroupViewModel[],
  startDate: string,
  ffFaGroupIdOnEmployment: boolean
): OptionType[] => {
  if (!startDate) {
    return [];
  }
  // Get the FAGroups that are valid on the chosen start date
  const filteredOptions = markupOptions.filter(({ validFrom, validTo }) => {
    if (isSameOrAfter(startDate, validFrom) && (!validTo || isSameOrBefore(startDate, validTo))) {
      return true;
    }
    return false;
  });
  /** Conform markupOptions to Select options */
  return filteredOptions.map(
    ({ faGroupId, fokText, fokId }): OptionType => ({
      id: ffFaGroupIdOnEmployment ? faGroupId : fokId,
      name: fokText || fokId,
    })
  );
};
